import {
  CheckCircleFilled,
  MinusCircleOutlined,
  WarningFilled
} from "@ant-design/icons"
import { Card, Flex, Form, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import useDebounce from "../../../hooks/use-debounce.ts"
import { calculateContractValidation } from "../ContractValidation/calculate-contract-validation.ts"
import {
  formatDurationLine,
  formatSalesPriceLine,
  type ValidationLine
} from "./format-proposal-validation.ts"
import type { ProposalValidationFormValues } from "./types.ts"

const { Text } = Typography

interface ProposalValidationCardProps<T extends ProposalValidationFormValues> {
  form: FormInstance<T>
  className?: string
}

/**
 * Compact "Proposal Validation" card — a guardrail, not a validator: it
 * never disables Save/Generate, and recalculates live (debounced) from the
 * form's own current values as the user edits Sales Price, any milestone
 * price/duration, or the Start/End Date, entirely client-side. Reuses
 * `calculateContractValidation` from `ContractValidation/` unchanged — the
 * calculation is document-agnostic already, see that module's own docstring
 * — rather than duplicating it under a Proposal-specific name. Only rendered
 * for the latest/editable Proposal version — enforced by the caller (the
 * parent page already refuses to render the editable form at all for a
 * historical version), not by a prop on this component.
 */
function ProposalValidationCard<T extends ProposalValidationFormValues>({
  form,
  className
}: ProposalValidationCardProps<T>) {
  const { t } = useTranslation()

  // Four scoped watches — not one selector over the whole values object —
  // so an edit anywhere else in this (potentially large) Proposal form never
  // restarts this card's debounce timer. `Form.useWatch`'s SELECTOR overload
  // returns `undefined` until the store's first change event after mount and
  // never reflects `initialValues` on the very first render (the same quirk
  // `TemplateDocumentForm`'s own `blocks` watch works around); the plain
  // NamePath overload used here reads the live value directly instead, so
  // no such fallback is needed.
  // `T` is generic (so callers can pass their own concrete
  // `FormInstance<TemplateDocumentFormData>` etc. without a cast at the call
  // site), which makes antd's own `NamePath<T>` typing for `useWatch`/
  // `getFieldValue` too strict to resolve a plain string literal — narrowed
  // to an untyped form handle here, internally only, for these four reads.
  const untypedForm = form as unknown as FormInstance<Record<string, unknown>>
  const salesPrice = Form.useWatch(["sales_price"], untypedForm)
  const startDate = Form.useWatch(["start_date"], untypedForm)
  const completionDate = Form.useWatch(["completion_date"], untypedForm)
  const phases = Form.useWatch(["phases"], untypedForm)

  const watched: ProposalValidationFormValues = {
    sales_price: salesPrice ?? untypedForm.getFieldValue("sales_price"),
    start_date: startDate ?? untypedForm.getFieldValue("start_date"),
    completion_date: completionDate ?? untypedForm.getFieldValue("completion_date"),
    phases: (phases ?? untypedForm.getFieldValue("phases")) as
      | ProposalValidationFormValues["phases"]
  }

  const debounced = useDebounce(watched, 400)

  const result = useMemo(() => calculateContractValidation(debounced), [debounced])

  const lines = useMemo(() => {
    const collected: Array<ValidationLine & { kind: "warning" | "insufficient_data" }> =
      []
    const salesPriceLine = formatSalesPriceLine(t, result.sales_price)
    if (salesPriceLine) {
      collected.push({
        ...salesPriceLine,
        kind: result.sales_price.status === "warning" ? "warning" : "insufficient_data"
      })
    }
    const durationLine = formatDurationLine(t, result.duration)
    if (durationLine) {
      collected.push({
        ...durationLine,
        kind: result.duration.status === "warning" ? "warning" : "insufficient_data"
      })
    }
    return collected
  }, [result, t])

  return (
    <Card
      size={"small"}
      className={className}
      title={t("proposalValidation.cardTitle")}
      data-testid={"proposal-validation-card"}
    >
      {lines.length === 0 ? (
        <Flex
          align={"center"}
          gap={8}
          data-testid={"proposal-validation-all-clear"}
        >
          <CheckCircleFilled style={{ color: "#52c41a" }} />
          <Text>{t("proposalValidation.allClear")}</Text>
        </Flex>
      ) : (
        <Flex vertical gap={10}>
          {lines.map((line, index) => (
            <Flex
              key={index}
              gap={8}
              align={"flex-start"}
              data-testid={`proposal-validation-line-${line.kind}`}
            >
              {line.kind === "warning" ? (
                <WarningFilled style={{ color: "#faad14", marginTop: 2 }} />
              ) : (
                <MinusCircleOutlined
                  style={{ color: "rgba(0, 0, 0, 0.45)", marginTop: 2 }}
                />
              )}
              <Flex vertical>
                <Text>{line.title}</Text>
                {line.comparison && (
                  <Text type={"secondary"} className={"text-xs"}>
                    {line.comparison}
                  </Text>
                )}
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}
    </Card>
  )
}

export { ProposalValidationCard }
